<template>
    <page :busy="busy" :tabs="tabs">
        <b-container fluid>
            <b-row>
                <b-col cols="12" sm="8" md="6" xl="4">
                    <div class="sms-setup-form d-flex flex-column py-3">
                        <validation-observer
                            ref="sms_gateway_form"
                            v-slot="{ errors, setFieldValue, meta }"
                            :initial-values="subscriberSMSGateway"
                        >
                            <validation-provider name="gateway" rules="required">
                                <form-select
                                    v-model="gateway"
                                    floatingLabel="SMS Gateway:"
                                    :options="smsGatewayChoices"
                                    :leftIcon="['fas', 'envelope']"
                                    :disabled="subscriberSMSGateway.connected"
                                    @change="(val) => setFieldValue('gateway', val)"
                                />
                            </validation-provider>
                            <template v-if="gateway > 0">
                                <validation-provider name="account_sid" rules="required" >
                                    <form-input
                                        floatingLabel="Account SID"
                                        :leftIcon="['fas', 'user']"
                                        v-model="subscriberSMSGateway.account_sid"
                                        :disabled="subscriberSMSGateway.connected"
                                        :error="errors.account_sid"
                                        @change="(val) => setFieldValue('account_sid', val)"
                                    ></form-input>
                                </validation-provider>
                                <validation-provider name="api_key_sid" rules="required">
                                    <form-input
                                        floatingLabel="API Key SID"
                                        :leftIcon="['fas', 'user']"
                                        v-model="subscriberSMSGateway.api_key_sid"
                                        :disabled="subscriberSMSGateway.connected"
                                        @change="(val) => setFieldValue('api_key_sid', val)"
                                        :error="errors.api_key_sid"
                                    ></form-input>
                                </validation-provider>
                                <validation-provider name="token" rules="required">
                                    <form-api-key-field
                                        class="mb-3"
                                        label="API Key Secret"
                                        :leftIcon="['fas', 'key']"
                                        v-model="subscriberSMSGateway.token"
                                        :disabled="subscriberSMSGateway.connected"
                                        :withSpace="false"
                                        :withCopy="false"
                                        :error="errors.token"
                                        @input="(val) => setFieldValue('token', val)"
                                    />
                                </validation-provider>
                            </template>

                            <div class="d-flex flex-wrap">
                                <b-button
                                    class="mr-1 mb-1"
                                    variant="primary"
                                    @click="saveGateway(false)"
                                    v-if="!subscriberSMSGateway.connected"
                                    :disabled="!meta.valid"
                                >
                                    Save SMS Gateway
                                </b-button>
                                <b-button
                                    class="mb-1"
                                    v-if="subscriberSMSGateway.connected"
                                    variant="danger"
                                    @click="disconnect"
                                >
                                    Disconnect
                                </b-button>
                            </div>
                        </validation-observer>
                    </div>

                    <div class="sms-guest-section">
                        <div class="twilio-info">
                            <a href="https://twilio.com/" target="_blank">
                                <img src="/static/images/twilio-logo.png" alt="Twilio Logo" class="twilio-logo" />
                            </a>
                            <div class="twilio-description">
                                <p>
                                    Lodgix has partnered with Twilio to offer transactional SMS to your guests. Twilio
                                    is the perfect solution for sending reliable and scalable transactional SMS. Whether
                                    it's sending check-in/check-out information, booking confirmations, or simple
                                    greetings, Twilio's global reach, real-time analytics, and industry-leading uptime
                                    make it the go-to choice for businesses that need to keep guests informed, engaged,
                                    and secure.
                                </p>
                            </div>
                            <div>
                                <b-button
                                    variant="primary"
                                    target="_blank"
                                    href="https://support.lodgix.com/en/articles/9676256-how-do-i-setup-twilio-to-integrate-with-lodgix"
                                >
                                    Learn More
                                </b-button>
                            </div>
                        </div>
                    </div>
                </b-col>
            </b-row>
        </b-container>
        <modal :id="disconnectConfirmModalID" title="Please Confirm" ok-title="OK" @ok="handleSMSGatewayDisconnect">
            Are you sure you want to disconnect from SMS Gateway?
        </modal>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import API from '../../api';
    import Page from '../generic/page/page.vue';
    import FormSelect from '../generic/form-select/form-select.vue';
    import FormInput from '../generic/form-input/form-input.vue';
    import Modal from '../generic/modal/modal.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import FormApiKeyField from '../generic/form-api-key-field/form-api-key-field.vue';
    import {
        SMS_GATEWAY_GET_DATA_ENDPOINT,
        SMS_GATEWAY_SAVE_ENDPOINT,
        SMS_GATEWAY_DISCONNECT_ENDPOINT,
    } from '../../endpoints';
    import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';

    const GATEWAY_NOT_SET = null;

    export default {
        name: 'SMSGatewaySetup',
        components: {
            Page,
            FormInput,
            FormSelect,
            ResponsiveButton,
            FormApiKeyField,
            Modal,
        },
        mixins: [removeScrollMixin],
        data() {
            return {
                busy: false,
                smsGatewayChoices: [],
                gateway: null,
                subscriberSMSGateway: {
                    gateway: null,
                    account_sid: '',
                    api_key_sid: '',
                    token: '',
                    connected: false,
                },
            };
        },
        computed: {
            tabs() {
                return [
                    {
                        title: 'SMS Gateway Setup',
                        route: '/app/sms/gateway/',
                        active: true,
                    },
                    {
                        title: 'Phone Numbers',
                        route: '/app/sms/numbers/',
                        active: false,
                        disabled: !this.subscriberSMSGateway.connected,
                    },
                    {
                        title: 'SMS Logs',
                        route: '/app/sms/logs/',
                        active: false,
                        disabled: !this.subscriberSMSGateway.connected,
                    },
                ];
            },
            disconnectConfirmModalID() {
                return 'disconnect-confirm-modal';
            },
        },
        async mounted() {
            await this.fetchData();
        },
        methods: {
            async fetchData() {
                try {
                    this.busy = true;
                    const { data } = await API.get(SMS_GATEWAY_GET_DATA_ENDPOINT());
                    const { subscriber_sms_gateway: subscriberSMSGateway, sms_gateway_choices: smsGatewayChoices } =
                        data;
                    this.smsGatewayChoices = smsGatewayChoices.map((choice) => {
                        return {
                            text: choice.name,
                            value: choice.id,
                        };
                    });
                    this.subscriberSMSGateway = subscriberSMSGateway;
                    this.gateway = this.subscriberSMSGateway.gateway || GATEWAY_NOT_SET;
                    this.$refs.sms_gateway_form.setValues(this.subscriberSMSGateway);
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch gateway choices' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchPhoneNumbers() {
                try {
                    this.displayWarnings = false;
                    const { data } = await API.get(SMS_GET_LISTS());
                    this.lists = data.lists.map((list) => ({
                        value: list.id,
                        text: list.name,
                    }));
                    if (!this.list && this.lists.length > 0) {
                        this.list = this.lists[0].id;
                    }
                    this.workingCredentials = data.working_credentials;
                    this.displayWarnings = true;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch phone numbers' });
                }
            },
            async saveGateway() {
                try {
                    this.busy = true;
                    await API.post(SMS_GATEWAY_SAVE_ENDPOINT(), {
                        gateway: this.gateway,
                        account_sid: this.subscriberSMSGateway.account_sid,
                        api_key_sid: this.subscriberSMSGateway.api_key_sid,
                        token: this.subscriberSMSGateway.token,
                    });
                    await this.fetchData();
                    this.successToast({ text: 'Successfully connected to SMS gateway' });
                } catch (e) {
                    this.errorToast({ text: 'Could not connect to SMS gateway' });
                } finally {
                    this.busy = false;
                }
            },
            async disconnect() {
                this.$bvModal.show(this.disconnectConfirmModalID);
            },
            async handleSMSGatewayDisconnect() {
                try {
                    this.busy = true;
                    await API.post(SMS_GATEWAY_DISCONNECT_ENDPOINT());
                    await this.fetchData();
                    this.successToast({ text: 'Successfully disconnected from SMS gateway.' });
                } catch (e) {
                    this.errorToast({ text: 'Could not disconnect' });
                } finally {
                    this.busy = false;
                }
            },
        },
    };
</script>

<style lang="css" scoped>
    .sms-guest-section {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 20px;
        background-color: #f9f9f9; /* Light background */
        border-radius: 8px; /* Rounded corners */
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Subtle shadow */
    }

    .twilio-info {
        display: flex;
        flex-direction: column; /* Stack elements vertically */
        align-items: flex-start; /* Align items to start */
        margin-top: 10px; /* Space between title and logo/text */
    }

    .twilio-logo {
        width: auto; /* Adjust size */
        height: 75px; /* Maintain aspect ratio */
        margin-bottom: 15px; /* Space below the logo */
        border-radius: 5px;
    }

    .twilio-description {
        margin-bottom: 15px; /* Space between description and link */
    }
</style>
